import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { PortableText } from '@portabletext/react'
import { client, urlFor } from '../lib/sanity'
import { Post } from '../types/sanity'
import BlogPostCard from './BlogPostCard'
import arrow from '../assets/icons/arrow-left-black.svg'

const components = {
  types: {
    image: ({ value }: any) => (
      <img
        src={urlFor(value).width(800).url()}
        alt={value.alt || ''}
        className="my-8 rounded-lg"
      />
    ),
  },
  marks: {
    link: ({ children, value }: any) => {
      const rel = !value.href.startsWith('/') ? 'noreferrer noopener' : undefined
      return (
        <a href={value.href} rel={rel} className="text-blue-600 hover:underline">
          {children}
        </a>
      )
    },
  },
  block: {
    normal: ({ children }: any) => {
      return <p className="body mb-8">{children}</p>
    }
  }
}

export default function BlogPost() {
  const navigate = useNavigate()
  const { slug } = useParams()
  const [post, setPost] = useState<Post | null>(null)
  const [nextPost, setNextPost] = useState<Post | null>(null)
  const [previousPost, setPreviousPost] = useState<Post | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Fetch current post
        const postQuery = `*[_type == "post" && slug.current == $slug][0]{
          _id,
          title,
          description,
          readTime,
          slug,
          author->{
            _id,
            name,
            slug,
            image
          },
          mainImage,
          secondaryImage,
          publishedAt,
          categories[]->{
            _id,
            title
          },
          body
        }`

        // Fetch next and previous posts
        const nextPostQuery = `*[_type == "post" && publishedAt > *[_type == "post" && slug.current == $slug][0].publishedAt][0]{
          _id,
          title,
          description,
          slug,
          mainImage,
          publishedAt
        }`

        const previousPostQuery = `*[_type == "post" && publishedAt < *[_type == "post" && slug.current == $slug][0].publishedAt] | order(publishedAt desc)[0]{
          _id,
          title,
          description,
          slug,
          mainImage,
          publishedAt
        }`

        const [currentPost, nextArticle, previousArticle] = await Promise.all([
          client.fetch(postQuery, { slug }),
          client.fetch(nextPostQuery, { slug }),
          client.fetch(previousPostQuery, { slug })
        ])

        setPost(currentPost)
        setNextPost(nextArticle)
        setPreviousPost(previousArticle)
        setLoading(false)
      } catch (err: any) {
        console.error('Error fetching posts:', err)
        setError(err.message)
        setLoading(false)
      }
    }

    fetchPosts()
  }, [slug])

  const handleGoBack = () => {
    navigate(-1)
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">Error: {error}</div>
  }

  if (loading) {
    return <div className="text-center py-8">Loading post...</div>
  }

  if (!post) {
    return <div className="text-center py-8 text-red-600">Post not found. Please check the URL or try another post.</div>
  }

  return (
    <article>
      {/* Back button */}
      <button 
        onClick={handleGoBack}
        className="hidden md:flex fixed top-20 left-8 z-10 w-16 h-16 bg-white rounded-full border border-gray-200 items-center justify-center hover:border-gray-300 transition-colors"
      >
        <img src={arrow} alt="←" className="w-10 h-10" />
      </button>

      {/* Banner section with overlaid title */}
      <div className="absolute left-0 right-0 top-0 h-[75vh] -mt-20 md:-mt-24" style={{ width: '100vw', marginLeft: 'calc(-50vw + 50%)' }}>
        {post.mainImage ? (
          <img
            src={urlFor(post.mainImage).width(2000).height(800).url()}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-lg text-gray-500">Sin imagen</span>
          </div>
        )}
        <div className="absolute inset-0 flex items-center bg-black bg-opacity-40">
          <div className="w-full md:w-1/2 max-w-[900px] mx-auto px-4 md:px-8">
            <h1 className="headline-large md:headline-xl text-white line-clamp-3 md:line-clamp-none">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Spacer for banner */}
      <div className="h-[70vh]"></div>

      {/* Content section */}
      <div className="w-screen flex justify-center mt-15">
        <div className="w-[750px] mx-auto px-4 md:px-0">
          {/* Metadata section */}
          <div className="flex flex-col space-y-6 mb-16">
            {/* Author and read time */}
            <div className="flex items-center space-x-4 text-gray-600">
              <div className="flex items-center">
                <div className="w-[30px] h-[30px] rounded-full overflow-hidden mr-3">
                  {post.author.image ? (
                    <img 
                      src={urlFor(post.author.image).width(60).height(60).url()} 
                      alt={post.author.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-full">
                      <span className="text-xs text-gray-500">Sin foto</span>
                    </div>
                  )}
                </div>
                <span>Por <Link to={`/autor/${post.author.slug.current}`} className="text-black hover:text-gray-600 transition-colors duration-300">{post.author.name}</Link></span>
              </div>
              <span>•</span>
              <span>{post.readTime} min read</span>
              <span>•</span>
              <span>{post.categories?.map(category => category.title).join(', ')}</span>
            </div>

            {/* Divider line */}
            <div className="w-full h-px bg-gray-200"></div>

            {/* Date */}
            <div className="text-gray-600">
              <h2 className="text-lg font-medium mb-2">Fecha</h2>
              <time className="body">{new Date(post.publishedAt).toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}</time>
            </div>
          </div>

          {/* Secondary Image */}
          {post.secondaryImage ? (
            <div className="mb-16">
              <img
                src={urlFor(post.secondaryImage).width(750).url()}
                alt={`${post.title} - secondary`}
                className="w-full rounded-lg"
              />
            </div>
          ) : null}

          {/* Description */}
          <div className="mb-16">
            <p className="body-large text-gray-600">
              {post.description}
            </p>
          </div>

          {/* Main content */}
          <div className="body space-y-8">
            <PortableText value={post.body} components={components} />
          </div>
        </div>
      </div>

      {/* Related Articles CTA */}
      {(nextPost || previousPost) && (
        <div className="w-screen flex justify-center mt-32 mb-32">
          <div className="w-full max-w-[1280px] px-4 md:px-8">
            <h2 className="headline-large mb-8">Sigue leyendo:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {previousPost && !nextPost && (
                <BlogPostCard post={previousPost} featured={true} />
              )}
              {nextPost && !previousPost && (
                <BlogPostCard post={nextPost} featured={true} />
              )}
              {previousPost && nextPost && (
                <>
                  <BlogPostCard post={previousPost} />
                  <BlogPostCard post={nextPost} />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </article>
  )
} 